import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Diamond, Lock, User, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      if (res.data.success) {
        // Store admin session
        localStorage.setItem("vk_admin_token", res.data.token);
        localStorage.setItem("vk_admin_user", username);
        toast.success("Welcome back, Admin!");
        navigate("/admin/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#C9A962]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#7B2D26]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md" data-testid="admin-login-page">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <Diamond className="w-12 h-12 text-[#C9A962]" />
          </div>
          <h1 className="text-3xl font-light text-white mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Admin Portal
          </h1>
          <p className="text-[#8B7355]">VK Gems & Jewels Management System</p>
        </div>

        {/* Login Form */}
        <div className="bg-[#0F0F0F] border border-[#C9A962]/20 p-8">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="w-5 h-5 text-[#C9A962]" />
            <h2 className="text-xl text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Secure Login</h2>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="text-xs text-[#8B7355] uppercase tracking-wider block mb-2">Username</label>
              <div className="relative">
                <User className="w-5 h-5 text-[#8B7355] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  required
                  className="w-full bg-[#0A0A0A] border border-[#C9A962]/20 text-white pl-12 pr-4 py-3 focus:outline-none focus:border-[#C9A962] transition-colors"
                  data-testid="admin-username"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-[#8B7355] uppercase tracking-wider block mb-2">Password</label>
              <div className="relative">
                <Lock className="w-5 h-5 text-[#8B7355] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  className="w-full bg-[#0A0A0A] border border-[#C9A962]/20 text-white pl-12 pr-12 py-3 focus:outline-none focus:border-[#C9A962] transition-colors"
                  data-testid="admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B7355] hover:text-[#C9A962] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A] font-semibold hover:from-[#D4B872] hover:to-[#9A7A24] transition-all disabled:opacity-50"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <span className="spinner mx-auto" />
              ) : (
                "Sign In to Dashboard"
              )}
            </button>
          </form>

          <p className="text-center text-[#8B7355] text-xs mt-6">
            Authorized personnel only. All access is logged.
          </p>
        </div>
      </div>
    </div>
  );
}
